// Quick action modals shared by the Leads list page and the Lead Details
// page (Update Stage / Status / Note / Follow-up / Send Email). Each modal is
// a controlled component: it receives the `lead`, calls back via `onConfirm`
// (or onSendTemplate / onSendCustom for email), and the parent owns the
// mutations and invalidation.
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Modal, Button, Select, Spinner } from "../../components/UI";
import { emailTemplatesAPI } from "../../services/api";
import {
  HiOutlineCheck,
  HiOutlineMail,
  HiOutlinePencil,
} from "react-icons/hi";

// Full status options for the quick status modal
export const fullStatusOptions = [
  { value: "cold", label: "Cold" },
  { value: "warm", label: "Warm" },
  { value: "hot", label: "Hot" },
];

// Follow-up type options
export const followUpTypeOptions = [
  { value: "call", label: "Call" },
  { value: "email", label: "Email" },
  { value: "visit", label: "Visit" },
  { value: "meeting", label: "Meeting" },
  { value: "other", label: "Other" },
];

// Quick Stage Modal Component
export const QuickStageModal = ({ isOpen, onClose, lead, stages, onConfirm, isLoading }) => {
  const [selectedStage, setSelectedStage] = useState("");

  useEffect(() => {
    if (isOpen && lead) {
      setSelectedStage(lead.stage || "");
    }
  }, [isOpen, lead]);

  const handleConfirm = () => {
    if (!selectedStage) {
      toast.error("Please select a stage");
      return;
    }
    onConfirm({ id: lead._id, stage: selectedStage });
  };

  if (!isOpen || !lead) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Update Stage" size="sm">
      <div className="space-y-3 sm:space-y-4">
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-sm text-gray-600">
            Updating stage for <span className="font-semibold text-gray-900">{lead.name}</span>
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            Current: <span className="font-medium">{stages.find(s => s.key === lead.stage)?.name || lead.stage?.replace(/_/g, " ") || "Unknown"}</span>
          </p>
        </div>

        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
            Select New Stage
          </label>
          <div className="space-y-1.5 max-h-64 overflow-y-auto">
            {stages.map((stage) => (
              <button
                key={stage.key}
                type="button"
                onClick={() => setSelectedStage(stage.key)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                  selectedStage === stage.key
                    ? "bg-primary-50 text-primary-700 border-2 border-primary-300"
                    : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50"
                }`}
              >
                <span
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: stage.color || "#6B7280" }}
                />
                <span className="font-medium">{stage.name}</span>
                {selectedStage === stage.key && (
                  <HiOutlineCheck className="w-4 h-4 ml-auto text-primary-600" />
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-2 sm:gap-3 justify-end pt-3 sm:pt-4">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleConfirm}
            loading={isLoading}
            disabled={!selectedStage || selectedStage === lead.stage}
          >
            Update Stage
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// Quick Status Modal Component
export const QuickStatusModal = ({ isOpen, onClose, lead, onConfirm, isLoading }) => {
  const [selectedStatus, setSelectedStatus] = useState("");

  useEffect(() => {
    if (isOpen && lead) {
      setSelectedStatus(lead.status || "");
    }
  }, [isOpen, lead]);

  const handleConfirm = () => {
    if (!selectedStatus) {
      toast.error("Please select a status");
      return;
    }
    onConfirm({ id: lead._id, status: selectedStatus });
  };

  if (!isOpen || !lead) return null;

  const statusColors = {
    new: "bg-blue-100 text-blue-700 border-blue-200",
    open: "bg-indigo-100 text-indigo-700 border-indigo-200",
    in_progress: "bg-purple-100 text-purple-700 border-purple-200",
    follow_up: "bg-amber-100 text-amber-700 border-amber-200",
    cold: "bg-cyan-100 text-cyan-700 border-cyan-200",
    warm: "bg-orange-100 text-orange-700 border-orange-200",
    hot: "bg-red-100 text-red-700 border-red-200",
    converted: "bg-green-100 text-green-700 border-green-200",
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Update Status" size="sm">
      <div className="space-y-3 sm:space-y-4">
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-sm text-gray-600">
            Updating status for <span className="font-semibold text-gray-900">{lead.name}</span>
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            Current: <span className="font-medium capitalize">{lead.status || "Unknown"}</span>
          </p>
        </div>

        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
            Select New Status
          </label>
          <div className="space-y-1.5 max-h-64 overflow-y-auto">
            {fullStatusOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setSelectedStatus(opt.value)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                  selectedStatus === opt.value
                    ? "bg-primary-50 text-primary-700 border-2 border-primary-300"
                    : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50"
                }`}
              >
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${statusColors[opt.value] || "bg-gray-100 text-gray-700 border-gray-200"}`}>
                  {opt.label}
                </span>
                {selectedStatus === opt.value && (
                  <HiOutlineCheck className="w-4 h-4 ml-auto text-primary-600" />
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-2 sm:gap-3 justify-end pt-3 sm:pt-4">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleConfirm}
            loading={isLoading}
            disabled={!selectedStatus || selectedStatus === lead.status}
          >
            Update Status
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// Quick Note Modal Component
export const QuickNoteModal = ({ isOpen, onClose, lead, onConfirm, isLoading }) => {
  const [noteText, setNoteText] = useState("");
  const [attachment, setAttachment] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setNoteText("");
      setAttachment(null);
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (!noteText.trim() && !attachment) {
      toast.error("Please enter a note or attach a file");
      return;
    }
    onConfirm({ id: lead._id, note: noteText.trim(), file: attachment });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAttachment(file);
    }
  };

  if (!isOpen || !lead) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Note" size="md">
      <div className="space-y-3 sm:space-y-4">
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-sm text-gray-600">
            Adding note to <span className="font-semibold text-gray-900">{lead.name}</span>
          </p>
          {lead.phone && (
            <p className="text-xs text-gray-500 mt-0.5">{lead.phone}</p>
          )}
        </div>

        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
            Note <span className="text-red-500">*</span>
          </label>
          <textarea
            rows={4}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Enter your note here..."
            className="w-full px-3.5 py-2.5 rounded-xl border-2 border-secondary-200 text-sm bg-white text-secondary-900 placeholder:text-secondary-400 focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none hover:border-secondary-300 transition-all duration-200 resize-y"
          />
        </div>

        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
            Attachment (optional)
          </label>
          <div className="flex items-center gap-2">
            <label className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-gray-300 text-sm text-gray-600 hover:bg-gray-50 hover:border-gray-400 cursor-pointer transition-colors">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
              Choose File
              <input
                type="file"
                className="hidden"
                onChange={handleFileChange}
                accept="image/*,.pdf,.doc,.docx,.txt"
              />
            </label>
            {attachment && (
              <span className="text-sm text-gray-600 truncate max-w-[200px]">
                {attachment.name}
              </span>
            )}
          </div>
        </div>

        <div className="flex gap-2 sm:gap-3 justify-end pt-3 sm:pt-4">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleConfirm}
            loading={isLoading}
            disabled={!noteText.trim() && !attachment}
          >
            Add Note
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// Quick Follow-up Modal Component
export const QuickFollowUpModal = ({ isOpen, onClose, lead, onConfirm, isLoading }) => {
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("10:00");
  const [followUpType, setFollowUpType] = useState("call");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (isOpen) {
      // Default to tomorrow
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setScheduledDate(tomorrow.toISOString().split("T")[0]);
      setScheduledTime("10:00");
      setFollowUpType("call");
      setNotes("");
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (!scheduledDate) {
      toast.error("Please select a date");
      return;
    }
    onConfirm({
      leadId: lead._id,
      scheduledDate,
      scheduledTime,
      type: followUpType,
      notes: notes.trim(),
    });
  };

  if (!isOpen || !lead) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Schedule Follow-up" size="md">
      <div className="space-y-3 sm:space-y-4">
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-sm text-gray-600">
            Scheduling follow-up for <span className="font-semibold text-gray-900">{lead.name}</span>
          </p>
          {lead.phone && (
            <p className="text-xs text-gray-500 mt-0.5">{lead.phone}</p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
              Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
              min={new Date().toISOString().split("T")[0]}
              className="w-full px-3.5 py-2.5 rounded-xl border-2 border-secondary-200 text-sm bg-white text-secondary-900 focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none hover:border-secondary-300 transition-all duration-200"
            />
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
              Time
            </label>
            <input
              type="time"
              value={scheduledTime}
              onChange={(e) => setScheduledTime(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-xl border-2 border-secondary-200 text-sm bg-white text-secondary-900 focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none hover:border-secondary-300 transition-all duration-200"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
            Type
          </label>
          <Select
            options={followUpTypeOptions}
            value={followUpType}
            onChange={(e) => setFollowUpType(e.target.value)}
          />
        </div>

        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
            Notes (optional)
          </label>
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add notes for this follow-up..."
            className="w-full px-3.5 py-2.5 rounded-xl border-2 border-secondary-200 text-sm bg-white text-secondary-900 placeholder:text-secondary-400 focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none hover:border-secondary-300 transition-all duration-200 resize-y"
          />
        </div>

        <div className="flex gap-2 sm:gap-3 justify-end pt-3 sm:pt-4">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleConfirm}
            loading={isLoading}
            disabled={!scheduledDate}
          >
            Schedule Follow-up
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// Quick Send Email Modal Component — two tabs:
//   • Template: lists all Email Templates from Settings; clicking one sends it
//     immediately (personalized with the lead's data) to this lead only.
//   • Custom: compose a brand-new email from scratch and send it.
export const QuickSendEmailModal = ({ isOpen, onClose, lead, onSendTemplate, onSendCustom, isLoading }) => {
  const [activeTab, setActiveTab] = useState("template"); // 'template' | 'custom'
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  // Fetch active Email Templates (same list as Settings → Email Templates)
  const { data: templatesData, isLoading: templatesLoading } = useQuery({
    queryKey: ["email-templates", "active"],
    queryFn: async () => {
      const response = await emailTemplatesAPI.getActive();
      return response.data?.data || [];
    },
    enabled: isOpen,
  });
  const templates = templatesData || [];

  // Fill {{variables}} from the lead (mirrors the backend fill logic)
  const fillVariables = (text) => {
    if (!text) return "";
    const varMap = {
      name: lead?.name || "",
      phone: lead?.phone || "",
      email: lead?.email || "",
      company: lead?.companyName || "",
      designation: lead?.designation || "",
      industry: lead?.industry || "",
      city: lead?.address?.city || "",
      state: lead?.address?.state || "",
      stage: (lead?.stage || "").replace(/_/g, " "),
      status: lead?.status || "",
      date: new Date().toLocaleDateString("en-IN"),
    };
    return String(text).replace(/\{\{(\w+)\}\}/g, (match, key) =>
      varMap[key] ? String(varMap[key]) : match,
    );
  };

  // Reset when the modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab("template");
      setSubject("");
      setMessage("");
    }
  }, [isOpen]);

  // Clicking a template sends it directly (no extra send button)
  const handleTemplateSend = (template) => {
    if (isLoading) return;
    onSendTemplate(template._id, {
      leadId: lead._id,
      subject: fillVariables(template.subject),
      message: fillVariables(template.message),
    });
  };

  const handleCustomSend = () => {
    if (!subject.trim()) {
      toast.error("Subject is required");
      return;
    }
    if (!message.trim()) {
      toast.error("Message is required");
      return;
    }
    onSendCustom({
      leadId: lead._id,
      subject: subject.trim(),
      message: message.trim(),
    });
  };

  if (!isOpen || !lead) return null;

  const tabButtonClass = (active) =>
    `flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border-2 transition-all duration-200 ${
      active
        ? "bg-primary-50 text-primary-700 border-primary-300"
        : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
    }`;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Send Email" size="lg">
      <div className="space-y-3 sm:space-y-4">
        {/* Lead info */}
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-sm text-gray-600">
            Sending email to <span className="font-semibold text-gray-900">{lead.name}</span>
          </p>
          <p className="text-xs text-gray-500 mt-0.5">{lead.email}</p>
        </div>

        {/* Two tabs side by side: Template | Custom */}
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setActiveTab("template")}
            className={tabButtonClass(activeTab === "template")}
          >
            <HiOutlineMail className="w-4 h-4" />
            Template
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("custom")}
            className={tabButtonClass(activeTab === "custom")}
          >
            <HiOutlinePencil className="w-4 h-4" />
            Custom
          </button>
        </div>

        {activeTab === "template" ? (
          /* Template list — click a template to send it directly */
          templatesLoading ? (
            <div className="flex justify-center py-6">
              <Spinner size="md" />
            </div>
          ) : templates.length === 0 ? (
            <div className="text-sm text-gray-500 bg-gray-50 rounded-lg p-4 text-center">
              No active email templates found. Add templates in Settings → Email Templates.
            </div>
          ) : (
            <>
              <p className="text-xs text-gray-500">
                Click a template to send it directly to this lead.
              </p>
              <div className="space-y-1.5 max-h-72 overflow-y-auto">
                {templates.map((template) => (
                  <button
                    key={template._id}
                    type="button"
                    disabled={isLoading}
                    onClick={() => handleTemplateSend(template)}
                    className="w-full px-3 py-2.5 rounded-lg text-left text-sm bg-white text-gray-700 border border-gray-200 hover:bg-primary-50 hover:border-primary-300 transition-colors disabled:opacity-50"
                  >
                    <div className="flex items-center gap-2">
                      <HiOutlineMail className="w-4 h-4 flex-shrink-0 text-gray-400" />
                      <span className="font-medium truncate">{template.name}</span>
                    </div>
                    <p className="text-xs text-gray-500 truncate mt-0.5 pl-6">
                      {template.subject}
                    </p>
                  </button>
                ))}
              </div>
            </>
          )
        ) : (
          /* Custom compose — write a new email from scratch */
          <>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
                Subject <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="e.g., Welcome to Our Company, {{name}}!"
                className="w-full px-3.5 py-2.5 rounded-xl border-2 border-secondary-200 text-sm bg-white text-secondary-900 placeholder:text-secondary-400 focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none hover:border-secondary-300 transition-all duration-200"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                rows={7}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Write your email message here..."
                className="w-full px-3.5 py-2.5 rounded-xl border-2 border-secondary-200 text-sm bg-white text-secondary-900 placeholder:text-secondary-400 focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none hover:border-secondary-300 transition-all duration-200 resize-y"
              />
              <p className="text-xs text-gray-500 mt-1">
                Variables like {"{{name}}"} are auto-filled from this lead's details.
              </p>
            </div>
            <div className="flex gap-2 sm:gap-3 justify-end pt-3 sm:pt-4">
              <Button variant="ghost" size="sm" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleCustomSend}
                loading={isLoading}
              >
                Send Email
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};